/**
 * Sales Summary Report Page
 */

import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, TrendingUp, DollarSign, Receipt, Calculator } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { DatePicker } from '@/components/ui/date-picker'
import { useUrlState } from '@/hooks/useUrlState'

export function SalesSummaryReport() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [dateFrom, setDateFrom] = useUrlState('desde')
  const [dateTo, setDateTo] = useUrlState('hasta')

  const { data: salesData, isLoading } = useQuery({
    queryKey: ['reports-sales-summary', dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (dateFrom) params.set('date_from', dateFrom)
      if (dateTo) params.set('date_to', `${dateTo}T23:59:59`)
      const qs = params.toString()
      const response = await api.get(`/reports/sales/summary${qs ? `?${qs}` : ''}`)
      return response.data
    },
  })

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('reports.salesSummary')}</h1>
          <p className="text-sm text-muted-foreground">
            {dateFrom || dateTo ? `${dateFrom || '…'} – ${dateTo || '…'}` : t('reports.thisMonth')}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <DatePicker
          value={dateFrom}
          onChange={setDateFrom}
          max={dateTo || undefined}
          placeholder={t('reports.from')}
          ariaLabel={t('reports.from')}
        />
        <DatePicker
          value={dateTo}
          onChange={setDateTo}
          min={dateFrom || undefined}
          placeholder={t('reports.to')}
          ariaLabel={t('reports.to')}
        />
      </div>

      {/* Stats Cards */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs md:text-sm text-muted-foreground">{t('reports.totalSales')}</p>
                <TrendingUp className="h-4 w-4 text-green-500" />
              </div>
              <p className="text-2xl md:text-3xl font-bold tabular-nums text-success">
                {formatCurrency(salesData?.total_sales || 0)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs md:text-sm text-muted-foreground">{t('reports.transactionsTitle')}</p>
                <Receipt className="h-4 w-4 text-blue-500" />
              </div>
              <p className="text-2xl md:text-3xl font-bold tabular-nums">
                {(salesData?.total_tickets || 0) + (salesData?.total_invoices || 0) + (salesData?.total_credit_notes || 0)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs md:text-sm text-muted-foreground">{t('reports.taxCollected')}</p>
                <DollarSign className="h-4 w-4 text-purple-500" />
              </div>
              <p className="text-2xl md:text-3xl font-bold tabular-nums text-purple-600">
                {formatCurrency(salesData?.total_tax || 0)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs md:text-sm text-muted-foreground">{t('reports.averageSale')}</p>
                <Calculator className="h-4 w-4 text-orange-500" />
              </div>
              <p className="text-2xl md:text-3xl font-bold tabular-nums">
                {formatCurrency(salesData?.average_sale || 0)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('reports.periodDetails')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/50">
                <span className="text-sm text-muted-foreground">{t('reports.subtotalNoTax')}</span>
                <span className="font-medium tabular-nums">{formatCurrency((salesData?.total_sales || 0) - (salesData?.total_tax || 0))}</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/50">
                <span className="text-sm text-muted-foreground">{t('reports.taxLabel')}</span>
                <span className="font-medium tabular-nums">{formatCurrency(salesData?.total_tax || 0)}</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-green-500/10">
                <span className="text-sm font-medium">{t('reports.total')}</span>
                <span className="font-bold text-success">{formatCurrency(salesData?.total_sales || 0)}</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/50">
                <span className="text-sm text-muted-foreground">{t('reports.averagePerSale')}</span>
                <span className="font-medium tabular-nums">{formatCurrency(salesData?.average_sale || 0)}</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
